import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Drawer, TextField } from "@outpave/ds";
import { Button } from "@mui/material";
import useAllocationForm from "../../../hooks/useAllocationForm";
import { useUnsavedChangesGuard } from "hooks/useUnsavedChangesGuard";
import { AllocationData } from "./AllocationTable";
import AllocationEditTypeEnum from "../../../enums/AllocationEditType";
import AllocationTypeDropdown from "components/common/AllocationTypeDropdown";
import AllocationSearchAutoComplete, {
  AllocationIdAndTypeProps,
} from "components/common/AllocationSearchAutoComplete";
import type { AllocationType } from "utils/allocations/allocationOptions";
import { Stack } from "components/layout/reusableLayouts";
import { fieldErrorText } from "utils/formikFieldError";

/**
 * The drawer serves four entry points, so the title has to name the one the
 * user picked; it read "Create New Allocation" even when editing.
 */
const titleByEditType: Record<AllocationEditTypeEnum, string> = {
  [AllocationEditTypeEnum.EDIT]: "Edit Allocation",
  [AllocationEditTypeEnum.ADD_CHILD]: "Add Child Allocation",
  [AllocationEditTypeEnum.NEW]: "Create New Allocation",
  [AllocationEditTypeEnum.NEW_WITH_TYPE]: "Create New Allocation",
};

interface AllocationDrawerProps {
  open: boolean;
  handleCloseDrawer: () => void;
  afterAllocationSubmit: (
    allocationIdAndType: AllocationIdAndTypeProps,
  ) => void;
  allocationToChange?: AllocationData;
  allocationTypeToChange?: AllocationType;
  editType?: AllocationEditTypeEnum;
  /** Fires once the drawer has slid out. */
  onAfterClose?: () => void;
}

const AllocationDrawer: React.FC<AllocationDrawerProps> = (props) => {
  const {
    open,
    handleCloseDrawer,
    afterAllocationSubmit,
    allocationToChange,
    allocationTypeToChange,
    editType,
    onAfterClose,
  } = props;
  const { t } = useTranslation();

  const { formikAllocation, addingAllocation, updatingAllocation } =
    useAllocationForm(afterAllocationSubmit);

  useEffect(() => {
    if (editType) {
      let newValues = {
        allocationId: "",
        allocationType: {
          key: "",
          name: "",
        },
        allocationCode: "",
        parentAllocation: { id: "", allocationCode: "" },
      };

      if (editType === AllocationEditTypeEnum.ADD_CHILD && allocationToChange) {
        newValues.allocationType = allocationToChange.allocationType;
        newValues.parentAllocation = {
          id: allocationToChange.id,
          allocationCode: allocationToChange.allocationCode,
        };
      } else if (
        editType === AllocationEditTypeEnum.EDIT &&
        allocationToChange
      ) {
        newValues.allocationId = allocationToChange.id;
        newValues.allocationType = allocationToChange.allocationType;
        newValues.allocationCode = allocationToChange.allocationCode;
        newValues.parentAllocation = {
          id: allocationToChange.parentAllocationId ?? "",
          allocationCode: allocationToChange.parentAllocationName ?? "",
        };
      } else if (editType === AllocationEditTypeEnum.NEW) {
        newValues = {
          allocationId: "",
          allocationType: {
            key: "",
            name: "",
          },
          allocationCode: "",
          parentAllocation: { id: "", allocationCode: "" },
        };
      } else if (
        editType === AllocationEditTypeEnum.NEW_WITH_TYPE &&
        allocationTypeToChange
      ) {
        newValues.allocationType = allocationTypeToChange;
      }

      formikAllocation.resetForm({ values: newValues });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editType, allocationToChange, allocationTypeToChange]);

  const filterDescendantsOfParent = useMemo(() => {
    if (
      editType === AllocationEditTypeEnum.EDIT &&
      allocationToChange?.id &&
      allocationToChange?.allocationType
    ) {
      return {
        allocationId: allocationToChange.id,
        allocationType: allocationToChange.allocationType,
      };
    }
    return null;
  }, [editType, allocationToChange]);

  const busy = addingAllocation || updatingAllocation;

  // A cancel resets the form once the drawer has slid out, so the fields hold still while it closes.
  const discardEdits = useRef(false);
  const { requestLeave, dialog } = useUnsavedChangesGuard(
    formikAllocation.dirty,
    () => {
      discardEdits.current = true;
      handleCloseDrawer();
    },
  );
  const handleClose = () => {
    if (busy) return;
    requestLeave();
  };
  const handleAfterClose = () => {
    if (discardEdits.current) formikAllocation.resetForm();
    discardEdits.current = false;
    onAfterClose?.();
  };

  return (
    <>
      <Drawer
        open={open}
        onClose={handleClose}
        onAfterClose={handleAfterClose}
        title={t(
          editType ? titleByEditType[editType] : "Create New Allocation",
        )}
        loading={busy}
        footer={
          <>
            <Button variant="primary-outlined" onClick={handleClose}>
              {t("Cancel")}
            </Button>

            <Button
              variant="primary-filled"
              disabled={
                busy || !formikAllocation.dirty || !formikAllocation.isValid
              }
              onClick={() => formikAllocation.handleSubmit()}
            >
              {t("Save")}
            </Button>
          </>
        }
      >
        <form onSubmit={formikAllocation.handleSubmit}>
          <Stack gap="var(--space-5)">
            <AllocationTypeDropdown
              value={formikAllocation.values.allocationType || null}
              onChange={(type) =>
                formikAllocation.setFieldValue("allocationType", type)
              }
              isEditable={true}
              showAll={true}
            />

            <AllocationSearchAutoComplete
              title={t("Parent Allocation")}
              allocation={formikAllocation.values.parentAllocation}
              allocationType={formikAllocation.values.allocationType}
              onSelectAllocation={(allocation) => {
                formikAllocation.setFieldValue("parentAllocation", {
                  id: allocation?.id,
                  allocationCode: allocation?.allocationCode,
                });
                formikAllocation.setFieldValue(
                  "allocationType",
                  allocation?.allocationType,
                );
              }}
              disabled={
                !formikAllocation.values.allocationType?.key ||
                formikAllocation.values.allocationType.key === ""
              }
              filterByAllocationType={true}
              filterDescendantsOfParent={filterDescendantsOfParent}
              showAll
            />

            <TextField
              name="allocationCode"
              label={t("Allocation Name")}
              required
              value={formikAllocation.values.allocationCode || ""}
              fullWidth
              onChange={formikAllocation.handleChange}
              onBlur={formikAllocation.handleBlur}
              errorText={fieldErrorText(formikAllocation, "allocationCode")}
            />
          </Stack>
        </form>
      </Drawer>
      {dialog}
    </>
  );
};

export default AllocationDrawer;
